import {
  UserGmtTimeZone,
  UserMeasure,
  UserOrganization,
  UserProfile,
} from '@/store/slices/user/types'
import {
  UserRole,
  OrganizationRole,
  MEASURE_NAME,
  MEASURE_ID,
} from '@/types/user'

// Shape returned by Identity Server GET /api/users/me
export interface IsUserProfileResponse {
  id: number
  email: string
  emailVerified: boolean
  phoneNumber: string | null
  phoneNumberVerified: boolean
  name: string | null
  givenName: string | null
  familyName: string | null
  middleName: string | null
  nickname: string | null
  preferredUsername: string | null
  profileUrl: string | null
  pictureUrl: string | null
  website: string | null
  gender: string | null
  birthdate: string | null
  locale: string | null
  zoneInfo: string | null // IANA tz string, e.g. "Asia/Singapore"
  measure: string | null // "Metric" | "Imperial"
  lastSeenChangelogVersion: string | null // e.g. "1.2.0"; null = never dismissed
  address: unknown
  isActive: boolean
  twoFactorEnabled: boolean
  hasPin: boolean
  lastLoginAt: string | null
  loginsCount: number
  createdAt: string
  updatedAt: string | null
  organizations: Array<{
    organizationId: number
    organizationName: string
    role: { id: number; name: string }
    joinedAt: string
  }>
  userMetadata: unknown
  roles: UserRole[]
}

// System role is taken from the IS top-level `roles` claim (UserRole[]).
const deriveSystemRole = (roles: IsUserProfileResponse['roles']): string => {
  if (roles.includes(UserRole.SuperAdmin)) return UserRole.SuperAdmin
  if (roles.includes(UserRole.Admin)) return UserRole.Admin
  return UserRole.User
}

// FE expects { id, name }. IS returns just the name string.
// Id preserved from legacy defaults: Metric=1, Imperial=2.
const MEASURE_BY_NAME: Record<string, UserMeasure> = {
  Metric: { id: 1, name: 'Metric' },
  Imperial: { id: 2, name: 'Imperial' },
}

const deriveMeasure = (measure: string | null): UserMeasure => {
  if (!measure) return MEASURE_BY_NAME.Metric
  const value = MEASURE_NAME[Number(measure) as MEASURE_ID]
  return MEASURE_BY_NAME[value] ?? MEASURE_BY_NAME.Metric
}

const deriveTimezoneDisplay = (zoneInfo: string): string => {
  // "Asia/Singapore" + current offset "+08:00" -> "Asia/Singapore (UTC+08:00)"
  const offset = computeIanaOffset(zoneInfo)
  return `${zoneInfo} (UTC${offset})`
}

// Returns offset in format "+08:00" / "-05:30" for the given IANA zone at "now".
const computeIanaOffset = (zoneInfo: string): string => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zoneInfo,
      timeZoneName: 'longOffset',
    }).formatToParts(new Date())
    const tzName = parts.find((p) => p.type === 'timeZoneName')?.value ?? ''
    // tzName examples: "GMT+08:00", "GMT-05:00", "GMT" (for UTC)
    if (tzName === 'GMT') return '+00:00'
    const match = tzName.match(/GMT([+-]\d{2}):?(\d{2})?/)
    if (!match) return '+00:00'
    const sign = match[1][0]
    const hh = match[1].slice(1)
    const mm = match[2] ?? '00'
    return `${sign}${hh}:${mm}`
  } catch {
    return '+00:00'
  }
}

const computeIanaHour = (zoneInfo: string): number => {
  const offset = computeIanaOffset(zoneInfo) // "+08:00"
  const sign = offset[0] === '-' ? -1 : 1
  const hours = parseInt(offset.slice(1, 3), 10)
  return sign * hours
}

const deriveTimeZone = (zoneInfo: string | null): string => {
  // Legacy format: "GMT08:00" (no sign separator between GMT and hour in some cases).
  // Kept for back-compat. Use offset-based display.
  if (!zoneInfo) return ''
  const offset = computeIanaOffset(zoneInfo) // "+08:00"
  return `GMT${offset}` // "GMT+08:00"
}

const deriveGmtTimeZone = (zoneInfo: string | null): UserGmtTimeZone | null => {
  // IS returns IANA string only. FE UserGmtTimeZone expects { id, zoneId, displayName, offset, hour }.
  // D2 decision (2026-04-13): Option A - FE derives all fields from zoneInfo via Intl API.
  // Known gap: numeric `id` is NOT derivable from IANA string. Set to 0 (placeholder).
  // This may break flows that depend on specific numeric ids (e.g., profile update requests
  // that send gmtTimeZoneId). Revisit if blocks progress.
  if (!zoneInfo) return null
  return {
    id: 0,
    zoneId: zoneInfo,
    displayName: deriveTimezoneDisplay(zoneInfo),
    offset: computeIanaOffset(zoneInfo),
    hour: computeIanaHour(zoneInfo),
  }
}

const deriveIsSuperUserOfAnyOrganization = (
  orgs: IsUserProfileResponse['organizations'],
): boolean => {
  return orgs.some((o) =>
    [OrganizationRole.Manager].includes(o.role?.name as OrganizationRole),
  )
}

const mapOrganizations = (
  orgs: IsUserProfileResponse['organizations'],
): UserOrganization[] => {
  return orgs.map((o) => ({
    id: o.organizationId,
    name: o.organizationName,
    role: o.role?.name ?? '',
  }))
}

export const mapIsUserToUserProfile = (
  is: IsUserProfileResponse,
): UserProfile => {
  return {
    id: is.id,
    name: is.name ?? is.email,
    email: is.email,
    role: deriveSystemRole(is.roles),
    organizations: mapOrganizations(is.organizations),
    measure: deriveMeasure(is.measure),
    language: is.locale ?? 'en',
    timeZone: deriveTimeZone(is.zoneInfo),
    gmtTimeZone: deriveGmtTimeZone(is.zoneInfo) as UserGmtTimeZone, // nullable at runtime; null check elsewhere
    status: is.isActive,
    twoFactorEnabled: is.twoFactorEnabled,
    avatar: is.pictureUrl ?? undefined,
    isSuperUserOfAnyOrganization: deriveIsSuperUserOfAnyOrganization(
      is.organizations,
    ),
    hasPin: is.hasPin ?? false,
    createdAt: is.createdAt,
    lastSeenChangelogVersion: is.lastSeenChangelogVersion ?? null,
  }
}
